import {
  Button,
  CheckIcon,
  CloseIcon,
  FlexBox,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useCheckoutRedirect } from "./useCheckoutRedirect"
import useGetSubOfferingCampaignPlan from "./useGetSubOfferingCampaignPlan"
import useDocumentTitle from "../../hooks/useDocumentTitle"
import useIsCoachingAvailable from "../../hooks/useIsCoachingAvailable"
import useMediaQuery from "../../hooks/useMediaQuery"
import { useUserQuery } from "../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { requireAuth } from "../../util/auth"
import { useHistory, useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"
import useSkipCoachingStep from "../coaching/useSkipCoachingStep"

const Paywall = () => {
  useDocumentTitle("Joon")
  const [isAtTop, setIsAtTop] = useState(true)
  const isSmallScreen = useMediaQuery("(max-height:750px)")
  const router = useRouter()
  const { user } = useUserQuery()
  const history = useHistory()
  const { t } = useTranslation()
  const isCoachingAvailable = useIsCoachingAvailable()
  const skipCoachingStep = useSkipCoachingStep()

  const { isLoadingCheckoutFn, redirectToFamilyCheckout } =
    useCheckoutRedirect()

  const campaignPlan = useGetSubOfferingCampaignPlan()

  const getOriginalPrice = (defaultPackagePrice: number) => {
    if (defaultPackagePrice < 100) {
      return defaultPackagePrice + 40
    } else {
      return defaultPackagePrice + 30
    }
  }

  const defaultPackagePrice = campaignPlan.price_info.unit_amount / 100 || 89.99
  const originalPrice = getOriginalPrice(defaultPackagePrice)

  useEffect(() => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_PAYWALL, {
      type: "daily",
    })
    localStorage.setItem(
      localStorageItems.lastSeenPaywall,
      dayjs().format("YYYY-MM-DD")
    )
    const handleScroll = () => {
      if (window.scrollY === 0) setIsAtTop(true)
      else setIsAtTop(false)
    }
    window.addEventListener("scroll", handleScroll)

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToBottom = () =>
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    })

  const onClickSkip = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_SKIP, {
      type: "daily",
    })
    if (history.length > 1) history.goBack()
    // Skipping coaching for a non-English user still has to run the rest of
    // onboarding, so hand off to the same skip the coaching landing page uses.
    else if (isCoachingAvailable) router.push("/coaching")
    else skipCoachingStep()
  }

  const howWeWillGetYouThere = [
    t("PAYWALL_MOF_BENEFIT_1"),
    t("PAYWALL_MOF_BENEFIT_2"),
    t("PAYWALL_MOF_BENEFIT_3"),
    t("PAYWALL_MOF_BENEFIT_4"),
  ]

  const messages = [
    t("PAYWALL_MOF_TESTIMONIAL_1"),
    t("PAYWALL_MOF_TESTIMONIAL_2"),
    t("PAYWALL_MOF_TESTIMONIAL_3"),
  ]

  if (!user) return <></>

  return (
    <div className="paywall-page" style={{ gap: SPACING.space4 }}>
      <img
        src={"/images/logos/logo_white.svg"}
        alt="Joon"
        className="joon-logo"
      />
      <button onClick={onClickSkip} className="close-button">
        <CloseIcon color={JoonUIColor.text.disabled} size={24} />
      </button>
      <div className="paywall-ft-title">{t("PAYWALL_MOF_TITLE")}</div>
      <FlexBox direction="column" gap={SPACING.space6}>
        {howWeWillGetYouThere.map((reason, index) => (
          <FlexBox key={index} align="center" gap={SPACING.space2} wrap={false}>
            <FlexBox
              align="center"
              gap={SPACING.space2}
              style={{
                backgroundColor: JoonUIColor.background.accent,
                borderRadius: SPACING.space2,
                padding: SPACING.space1,
                width: SPACING.space6,
                height: SPACING.space6,
              }}
            >
              <CheckIcon color={JoonUIColor.icon.inverted} size={16} />
            </FlexBox>
            <Typography variant="body" style={{ lineHeight: "20px" }}>
              {reason}
            </Typography>
          </FlexBox>
        ))}
      </FlexBox>
      <FlexBox
        direction="column"
        gap={SPACING.space4}
        style={{ marginTop: SPACING.space4 }}
      >
        <Typography
          variant="h3"
          color={JoonUIColor.text.primaryAccent}
          textAlign="center"
        >
          {t("PAYWALL_MOF_TESTIMONIALS_TITLE")}
        </Typography>
        {messages.map((message, index) => (
          <ChatBubble
            key={index}
            message={message}
            side={index % 2 === 0 ? "left" : "right"}
          />
        ))}
      </FlexBox>

      <div className="paywall-action-box">
        {isAtTop && isSmallScreen && (
          <button onClick={scrollToBottom} className="scroll-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="12"
              viewBox="0 0 40 14"
            >
              <path
                d="M0 0 L20 12 L40 0"
                fill="none"
                stroke="white"
                stroke-width="4"
              />
            </svg>
          </button>
        )}
        <div className="paywall-action-box__text">
          {t("PAYWALL_MOF_DISCOUNT_BANNER")}
        </div>
        <div className="paywall-price">
          <span className="paywall-price__old">
            ${originalPrice.toFixed(2)}
          </span>
          <Typography variant="body">
            {t("PAYWALL_MOF_PRICE", { price: defaultPackagePrice.toFixed(2) })}
          </Typography>
        </div>
        <Button
          isLoading={isLoadingCheckoutFn}
          onClick={() => {
            redirectToFamilyCheckout(campaignPlan.campaign)
            localStorage.removeItem(localStorageItems.therapistCode)
            trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_START_TRIAL)
          }}
          style={{
            fontSize: "24px",
            background: "#f39f3d",
            borderColor: "#f39f3d",
            width: "100%",
            marginTop: SPACING.space2,
            marginBottom: SPACING.space6,
          }}
          text={t("PAYWALL_MOF_UNLOCK")}
          bold
        />
      </div>
    </div>
  )
}

const PaywallMOFWithAuth = requireAuth(Paywall)
export default PaywallMOFWithAuth

const ChatBubble = ({
  message,
  side,
}: {
  message: string
  side: "left" | "right"
}) => {
  const isLeft = side === "left"
  return (
    <div
      style={{
        backgroundColor: JoonUIColor.background.accent,
        borderRadius: SPACING.space4,
        padding: SPACING.space3,
        position: "relative",
        width: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: "-10px",
          left: isLeft ? "90%" : "10%",
          borderWidth: "10px",
          borderStyle: "solid",
          borderColor: isLeft
            ? `transparent ${JoonUIColor.background.accent} transparent transparent`
            : `transparent transparent transparent ${JoonUIColor.background.accent}`,
        }}
      />
      <Typography variant="body" color={JoonUIColor.text.inverted}>
        {message}
      </Typography>
    </div>
  )
}
